// Poll answering and results, kept out of the components so they can be
// tested: a module that imports lit.js cannot load under `bun test`.
//
// A selection is what a member has picked on one question so far:
//   { optionIds: string[], notListed: boolean, notListedText: string, text: string }
// and selections map question ids to them.

/** The longest written answer the API accepts. */
export const MAX_ANSWER_LENGTH = 5000;

/** @returns {{optionIds: string[], notListed: boolean, notListedText: string, text: string}} */
export function emptySelection() {
    return { optionIds: [], notListed: false, notListedText: '', text: '' };
}

/**
 * The selections a saved response stands for, so changing your answers starts
 * from what you said.
 * @param {{answers?: Array<{questionId: string, optionIds?: string[], notListedText?: string, text?: string}>}|null|undefined} response
 * @returns {Record<string, ReturnType<typeof emptySelection>>}
 */
export function selectionsFromResponse(response) {
    /** @type {Record<string, ReturnType<typeof emptySelection>>} */
    const selections = {};
    for (const answer of response?.answers ?? []) {
        selections[answer.questionId] = {
            optionIds: [...(answer.optionIds ?? [])],
            notListed: Boolean(answer.notListedText),
            notListedText: answer.notListedText ?? '',
            text: answer.text ?? '',
        };
    }
    return selections;
}

/**
 * Pick an option. On a pick-one question it replaces whatever was picked,
 * "Not listed" included; on a pick-any question it toggles.
 * @param {ReturnType<typeof emptySelection>} selection
 * @param {string} optionId
 * @param {boolean} allowMultiple
 */
export function pickOption(selection, optionId, allowMultiple) {
    if (!allowMultiple) return { ...selection, optionIds: [optionId], notListed: false };
    const optionIds = selection.optionIds.includes(optionId)
        ? selection.optionIds.filter((id) => id !== optionId)
        : [...selection.optionIds, optionId];
    return { ...selection, optionIds };
}

/**
 * Pick "Not listed". On a pick-one question it replaces any option; on a
 * pick-any question it toggles. What was written in it is kept either way, so
 * unticking by mistake does not lose it.
 * @param {ReturnType<typeof emptySelection>} selection
 * @param {boolean} allowMultiple
 */
export function pickNotListed(selection, allowMultiple) {
    if (!allowMultiple) return { ...selection, optionIds: [], notListed: true };
    return { ...selection, notListed: !selection.notListed };
}

/**
 * The body of PUT /polls/{id}/response, leaving out questions with nothing
 * answered.
 * @param {{questions: Array<{id: string, kind: string}>}} poll
 * @param {Record<string, ReturnType<typeof emptySelection>>} selections
 * @returns {Array<{questionId: string, optionIds?: string[], notListedText?: string, text?: string}>}
 */
export function answersPayload(poll, selections) {
    const answers = [];
    for (const question of poll.questions) {
        const selection = selections[question.id];
        if (!selection) continue;

        if (question.kind === 'text') {
            const text = selection.text.trim();
            if (text) answers.push({ questionId: question.id, text });
            continue;
        }

        const notListedText = selection.notListed ? selection.notListedText.trim() : '';
        if (selection.optionIds.length === 0 && !notListedText) continue;
        const answer = { questionId: question.id, optionIds: [...selection.optionIds] };
        if (notListedText) answer.notListedText = notListedText;
        answers.push(answer);
    }
    return answers;
}

/**
 * What stops these selections being submitted, or '' when nothing does. The
 * API checks all of this too; this is so the member hears it before sending.
 * @param {{questions: Array<{id: string, kind: string}>}} poll
 * @param {Record<string, ReturnType<typeof emptySelection>>} selections
 * @returns {string}
 */
export function validateSelections(poll, selections) {
    for (const [index, question] of poll.questions.entries()) {
        const selection = selections[question.id];
        if (!selection) continue;
        const number = index + 1;

        if (question.kind === 'text' && selection.text.trim().length > MAX_ANSWER_LENGTH) {
            return `Your answer to question ${number} is longer than ${MAX_ANSWER_LENGTH} characters.`;
        }
        if (question.kind === 'choice' && selection.notListed) {
            const text = selection.notListedText.trim();
            if (!text) return `Say what's not listed in question ${number}.`;
            if (text.length > MAX_ANSWER_LENGTH) {
                return `Your answer to question ${number} is longer than ${MAX_ANSWER_LENGTH} characters.`;
            }
        }
    }
    if (answersPayload(poll, selections).length === 0) return 'Answer at least one question.';
    return '';
}

/**
 * A whole-number percentage, 0 when there is nothing to divide by.
 * @param {number} count
 * @param {number} total
 */
export function percent(count, total) {
    if (!total || total < 1) return 0;
    return Math.round((count / total) * 100);
}

/**
 * The bars for one choice question's results: each option, then "Not listed"
 * when the question offers it. Percentages are of the respondents who answered
 * this question, so on a pick-any question they can add up to more than 100.
 * @param {{allowNotListed?: boolean}} question
 * @param {{answered: number, notListed: number, options: Array<{optionId: string, label: string, count: number}>}} result
 * @returns {Array<{label: string, count: number, percent: number, notListed: boolean}>}
 */
export function tallyRows(question, result) {
    const rows = result.options.map((option) => ({
        label: option.label,
        count: option.count,
        percent: percent(option.count, result.answered),
        notListed: false,
    }));
    if (question.allowNotListed) {
        rows.push({
            label: 'Not listed',
            count: result.notListed,
            percent: percent(result.notListed, result.answered),
            notListed: true,
        });
    }
    return rows;
}

/**
 * A question's prompt as one line of plain text, for the results panel: its
 * first line, without images or Markdown punctuation. Rendering the whole
 * prompt again — screenshots included — would repeat the poll.
 * @param {string} prompt
 */
export function promptSummary(prompt) {
    const firstLine = prompt.split('\n').find((line) => line.trim() !== '') ?? '';
    return firstLine
        .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
        .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
        .replace(/[#*_`>]/g, '')
        .trim();
}

/**
 * The badge a poll wears in the list.
 * @param {{state: string, answered: boolean}} poll
 * @returns {{label: string, badge: string}}
 */
export function pollBadge(poll) {
    if (poll.state === 'closed') return { label: 'Closed', badge: 'text-bg-secondary' };
    if (poll.answered) return { label: 'Answered', badge: 'text-bg-success' };
    return { label: 'Not answered yet', badge: 'text-bg-primary' };
}

/**
 * How many open polls the member has not answered — what the home page nudges
 * them towards.
 * @param {Array<{state: string, answered: boolean}>} polls
 */
export function unansweredCount(polls) {
    return polls.filter((p) => p.state === 'open' && !p.answered).length;
}
